import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, MailOpen, Mail, Send, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { api, enc, errMsg } from "@/lib/api";
import { cn } from "@/lib/utils";

const fmt = (d) => (d ? new Date(d).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }) : "");

const Bubble = ({ m }) => {
  const inbound = m.direction === "in";
  return (
    <div className={cn("max-w-[85%] rounded-xl border px-3 py-2 text-sm", inbound ? "self-start border-[#27272A] bg-[#18181B] text-zinc-100" : "self-end border-[#D4AF37]/30 bg-[#D4AF37]/10 text-zinc-100")} data-testid={`thread-msg-${m.id}`}>
      <div className="mb-1 flex flex-wrap gap-x-2 text-[10px] text-zinc-500">
        <span className={inbound ? "text-sky-300" : "text-[#F3C649]"}>{inbound ? m.from || "Lead" : "You"}</span>
        <span>{fmt(m.created_at)}</span>
        {m.subject && <span className="truncate">· {m.subject}</span>}
      </div>
      <p className="whitespace-pre-wrap break-words">{m.text}</p>
    </div>
  );
};

// Spec §28: GET .../thread, POST .../reply, POST .../reply/suggest, PATCH {replies_read}.
export const LeadThread = ({ lead, onChange }) => {
  const [msgs, setMsgs] = useState(null);
  const [missing, setMissing] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState("");
  const base = `/admin/resume-scans/${enc(lead.scan_id)}`;

  useEffect(() => {
    let live = true;
    api.get(`${base}/thread`).then((r) => {
      if (!live) return;
      setMsgs(r.data?.messages || []);
      if (lead.unread_replies) markRead(true, true);
    }).catch((e) => { if (!live) return; if (e.response?.status === 404) setMissing(true); else toast.error(errMsg(e)); setMsgs([]); });
    return () => { live = false; };
  }, [lead.scan_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const markRead = async (read, silent) => {
    const prev = lead.unread_replies || 0;
    onChange({ unread_replies: read ? 0 : 1 });
    try { await api.patch(base, { replies_read: read }); if (!silent) toast.success(read ? "Marked as read" : "Marked as unread"); }
    catch (e) { onChange({ unread_replies: prev }); if (!silent) toast.error(errMsg(e)); }
  };

  const suggest = async () => {
    setBusy("suggest");
    try { const { data } = await api.post(`${base}/reply/suggest`, {}); setText(data?.text || ""); }
    catch (e) { toast.error(e.response?.status === 404 ? "AI suggestions are coming soon (backend spec §28)." : errMsg(e)); } finally { setBusy(""); }
  };

  const send = async () => {
    setBusy("send");
    try {
      const { data } = await api.post(`${base}/reply`, { text: text.trim() });
      const m = data?.message || { id: `local_${Date.now()}`, direction: "out", text: text.trim(), created_at: new Date().toISOString() };
      setMsgs((x) => [...(x || []), m]); setText("");
      onChange({ status: "contacted" });
      toast.success(`Reply sent to ${lead.email}`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };

  if (missing) return <p className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-200" data-testid={`thread-pending-${lead.scan_id}`}>Reply inbox coming soon. It fills in once the backend adds the lead reply endpoints (backend spec section 28).</p>;
  if (!msgs) return <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-[#D4AF37]" /></div>;

  return (
    <div className="space-y-3" data-testid={`thread-${lead.scan_id}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-[11px] uppercase tracking-wider text-zinc-500">Conversation</span>
        {msgs.some((m) => m.direction === "in") && (
          <button onClick={() => markRead(!!lead.unread_replies)} className="flex items-center gap-1 text-xs text-zinc-400 hover:text-zinc-100" data-testid={`thread-toggle-read-${lead.scan_id}`}>
            {lead.unread_replies ? <><MailOpen className="h-3.5 w-3.5" />Mark as read</> : <><Mail className="h-3.5 w-3.5" />Mark as unread</>}
          </button>
        )}
      </div>
      {msgs.length === 0 ? <p className="text-xs text-zinc-500" data-testid={`thread-empty-${lead.scan_id}`}>No emails in this conversation yet.</p>
        : <div className="flex max-h-80 flex-col gap-2 overflow-y-auto pr-1">{msgs.map((m) => <Bubble key={m.id} m={m} />)}</div>}
      {lead.unsubscribed ? <p className="text-xs text-red-300">This lead unsubscribed, so replies are turned off.</p> : (
        <div className="space-y-2">
          <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={4} placeholder={`Reply to ${lead.email}…`} className="bg-[#09090B]" data-testid={`thread-reply-input-${lead.scan_id}`} />
          <div className="flex flex-wrap justify-end gap-2">
            <Button size="sm" variant="outline" onClick={suggest} disabled={!!busy} data-testid={`thread-suggest-${lead.scan_id}`}>{busy === "suggest" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}Suggest with AI</Button>
            <Button size="sm" onClick={send} disabled={!!busy || !text.trim()} data-testid={`thread-send-${lead.scan_id}`}>{busy === "send" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}Send reply</Button>
          </div>
        </div>
      )}
    </div>
  );
};
